import type { OptionType } from 'components/DesignSystems/New/PratusSelect';

/**
 * The ICS organisational element a position represents.
 *
 * Kept in step with two other places, none of which can import this file:
 *
 * - POSITION_CATEGORIES in services/coms/src/routes/ICS/utils/positionCategory.ts
 * - The ics_position_category_check constraint (see
 *   services/coms/src/database/migrations/2026-07-27-position-category.ts and
 *   2026-07-31-position-category-staging-area.ts)
 *
 * Values are the display strings themselves, so nothing has to translate
 * between the database, the API and this dropdown.
 */
export const POSITION_CATEGORIES = [
  'None',
  'Section',
  'Branch',
  'Division',
  'Group',
  'Staging Area',
  'Strike Team',
  'Task Force',
  'Single Resource',
  'Custom',
] as const;

export type PositionCategory = (typeof POSITION_CATEGORIES)[number];

/** The one category that carries a user-supplied label alongside it. */
export const CUSTOM_POSITION_CATEGORY: PositionCategory = 'Custom';

export const POSITION_CATEGORY_OPTIONS: OptionType[] = POSITION_CATEGORIES.map(
  (category) => ({ value: category, label: category })
);

/**
 * What to show for a position's category, or null when there is nothing worth
 * showing.
 *
 * 'None' and an unlabelled 'Custom' both collapse to null so callers can skip
 * rendering entirely rather than printing a meaningless row.
 */
export const resolveCategoryLabel = (
  category: string | null | undefined,
  customCategory: string | null | undefined
): string | null => {
  if (!category || category === 'None') return null;
  if (category === CUSTOM_POSITION_CATEGORY) return customCategory || null;
  return category;
};
